import { CARD_W, CARD_H, type ClassicSlot } from './types.js';

export const CLASSIC_LAYOUT = {
  CARD_W,
  CARD_H,
  COL_GAP: 14,
  ROW_GAP: 28,
  FAN_DOWN: 22,
} as const;

export interface GridRow {
  /** y-coordinate of the row (top of the card slot) */
  y: number;
  /** if true, cards in piles in this row cascade down */
  fanDown?: boolean;
  /** ordered pile IDs for each column position; null = empty column */
  slots: ReadonlyArray<string | null>;
}

export interface GridDef {
  cols: number;
  /** felt left padding before column 0 (default centers grid in feltWidth) */
  startX?: number;
  /** felt total width in px - used to auto-center if startX is omitted */
  feltWidth?: number;
  rows: ReadonlyArray<GridRow>;
}

export interface SlotResolver {
  slot(pileId: string): ClassicSlot;
  /** the resolved pile -> slot map (read-only). useful for iteration. */
  all(): ReadonlyMap<string, ClassicSlot>;
}

export function createGrid(def: GridDef): SlotResolver {
  const { cols, rows } = def;
  const stride = CARD_W + CLASSIC_LAYOUT.COL_GAP;
  const totalWidth = cols * CARD_W + (cols - 1) * CLASSIC_LAYOUT.COL_GAP;
  const startX = def.startX ?? (def.feltWidth != null
    ? Math.round((def.feltWidth - totalWidth) / 2)
    : 0);
  const map = new Map<string, ClassicSlot>();
  for (const row of rows) {
    row.slots.forEach((pileId, i) => {
      if (!pileId) return;
      map.set(pileId, {
        x: startX + i * stride,
        y: row.y,
        fanDown: !!row.fanDown,
      });
    });
  }
  return {
    slot(id) {
      const s = map.get(id);
      if (!s) throw new Error(`unknown pile: ${id}`);
      return s;
    },
    all: () => map,
  };
}

/** Free-form resolver for irregular boards (peaks, pyramids). */
export function fromMap(map: Record<string, ClassicSlot>): SlotResolver {
  const m = new Map(Object.entries(map));
  return {
    slot(id) {
      const s = m.get(id);
      if (!s) throw new Error(`unknown pile: ${id}`);
      return s;
    },
    all: () => m,
  };
}
